'use client'

import { uploadMedia } from '@/lib/api/atendimento/chat'
import { getDepartmentsByOrganization } from '@/lib/api/atendimento/departments'
import {
    QUICK_MESSAGE_VISIBILITY_DESCRIPTIONS,
    QUICK_MESSAGE_VISIBILITY_LABELS,
    QuickMessageVisibility,
    createQuickMessage,
    getQuickMessage,
    updateQuickMessage,
} from '@/lib/api/messaging/quick-messages-api'
import {
    TemplateToken,
    getWhatsappTemplateTokens,
} from '@/lib/api/messaging/whatsapp-templates-api'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useAuth } from '@/store/hooks'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
    MdArrowBack,
    MdAttachFile,
    MdBolt,
    MdClose,
    MdSave,
} from 'react-icons/md'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

const LIST_ROUTE = '/messaging/quick-messages'

const VISIBILITIES: QuickMessageVisibility[] = [
    'PRIVATE',
    'COMPANY',
    'ORGANIZATION',
]

const TOKEN_ENTITY_LABELS: Record<TemplateToken['entity'], string> = {
    client: 'Cliente',
    user: 'Usuário',
    company: 'Empresa',
    context: 'Data/hora',
}

const labelClass = 'text-sm font-medium text-base-content/80'

interface FormState {
    trigger: string
    title: string
    body: string
    visibility: QuickMessageVisibility
    departmentId: string
    active: boolean
}

const EMPTY_FORM: FormState = {
    trigger: '',
    title: '',
    body: '',
    visibility: 'COMPANY',
    departmentId: '',
    active: true,
}

/** O atalho é digitado depois da "/": sem espaço, sem acento, minúsculo. */
function sanitizeTrigger(raw: string): string {
    return raw
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/^\/+/, '')
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9_-]/g, '')
        .slice(0, 40)
}

function fileNameFromUrl(url: string): string {
    try {
        return decodeURIComponent(url.split('/').pop() || url)
    } catch {
        return url
    }
}

export default function QuickMessageFormPage() {
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const isEditing = !!id
    const { canCreate, canUpdate } = useResourcePermissions(
        'messaging',
        'quick_messages'
    )
    const { user } = useAuth()

    const [form, setForm] = useState<FormState>(EMPTY_FORM)
    const [mediaUrls, setMediaUrls] = useState<string[]>([])
    const [saving, setSaving] = useState(false)
    const [uploading, setUploading] = useState(false)
    const [loading, setLoading] = useState(false)

    const [tokens, setTokens] = useState<TemplateToken[]>([])
    const [departments, setDepartments] = useState<
        Array<{ id: number; name: string }>
    >([])

    const bodyRef = useRef<HTMLTextAreaElement>(null)
    const fileInputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
        getWhatsappTemplateTokens()
            .then(res => {
                if (res.success) setTokens(res.result)
            })
            .catch(() => {})
    }, [])

    useEffect(() => {
        if (!user?.organizationId) return
        getDepartmentsByOrganization(user.organizationId)
            .then(res => {
                if (res.success && Array.isArray(res.result)) {
                    setDepartments(
                        res.result.map(d => ({ id: d.id, name: d.name }))
                    )
                }
            })
            .catch(() => {})
    }, [user?.organizationId])

    useEffect(() => {
        if (!isEditing || !id) return
        setLoading(true)
        getQuickMessage(Number(id))
            .then(res => {
                if (res.success && res.result) {
                    const msg = res.result
                    setForm({
                        trigger: msg.trigger,
                        title: msg.title,
                        body: msg.body,
                        visibility: msg.visibility,
                        departmentId: msg.departmentId
                            ? String(msg.departmentId)
                            : '',
                        active: msg.active,
                    })
                    setMediaUrls(msg.mediaUrls ?? [])
                } else {
                    toast.error(res.error || 'Mensagem rápida não encontrada')
                    navigate(LIST_ROUTE)
                }
            })
            .catch(() => {
                toast.error('Erro ao carregar mensagem rápida')
                navigate(LIST_ROUTE)
            })
            .finally(() => setLoading(false))
    }, [id, isEditing, navigate])

    // Agrupa preservando a ordem do catálogo do backend, que já vem por
    // entidade — reordenar aqui só criaria uma segunda fonte de verdade.
    const groupedTokens = useMemo(() => {
        const groups: Array<{
            entity: TemplateToken['entity']
            label: string
            items: TemplateToken[]
        }> = []
        for (const token of tokens) {
            let group = groups.find(g => g.entity === token.entity)
            if (!group) {
                group = {
                    entity: token.entity,
                    label: TOKEN_ENTITY_LABELS[token.entity],
                    items: [],
                }
                groups.push(group)
            }
            group.items.push(token)
        }
        return groups
    }, [tokens])

    const tokenExamples = useMemo(
        () => new Map(tokens.map(t => [t.key, t.example])),
        [tokens]
    )

    // Pré-visualização: troca cada {{token}} pelo exemplo do catálogo, para o
    // autor ver o texto como o cliente vai receber.
    const preview = useMemo(
        () =>
            form.body.replace(
                /\{\{\s*([a-zA-Z][a-zA-Z0-9_.]*)\s*\}\}/g,
                (full, key) => tokenExamples.get(key) ?? full
            ),
        [form.body, tokenExamples]
    )

    const insertToken = (key: string) => {
        const snippet = `{{${key}}}`
        const el = bodyRef.current
        if (!el) {
            setForm(f => ({ ...f, body: f.body + snippet }))
            return
        }
        const start = el.selectionStart ?? el.value.length
        const end = el.selectionEnd ?? el.value.length
        const next = form.body.slice(0, start) + snippet + form.body.slice(end)
        setForm(f => ({ ...f, body: next }))
        requestAnimationFrame(() => {
            el.focus()
            const pos = start + snippet.length
            el.setSelectionRange(pos, pos)
        })
    }

    const handleFileSelect = async (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const files = Array.from(event.target.files || [])
        if (files.length === 0) return

        if (mediaUrls.length + files.length > 5) {
            toast.warn('Máximo de 5 anexos por mensagem rápida')
            return
        }

        setUploading(true)
        try {
            const res = await uploadMedia(files)
            if (res.success && res.result) {
                setMediaUrls(prev => [
                    ...prev,
                    ...res.result.files.map(f => f.url),
                ])
            } else {
                toast.error('Erro ao enviar os anexos')
            }
        } catch {
            toast.error('Falha no envio dos anexos')
        } finally {
            setUploading(false)
            if (fileInputRef.current) fileInputRef.current.value = ''
        }
    }

    const handleSubmit = async () => {
        const trigger = sanitizeTrigger(form.trigger)

        if (!trigger || trigger.length < 2) {
            toast.warn('Informe um atalho com pelo menos 2 caracteres')
            return
        }
        if (!form.title.trim()) {
            toast.warn('Informe um título para identificar a mensagem')
            return
        }
        if (!form.body.trim()) {
            toast.warn('Escreva o texto da mensagem')
            return
        }

        const payload = {
            trigger,
            title: form.title.trim(),
            body: form.body,
            mediaUrls,
            visibility: form.visibility,
            departmentId: form.departmentId
                ? Number(form.departmentId)
                : undefined,
            active: form.active,
        }

        setSaving(true)
        try {
            const res = isEditing
                ? await updateQuickMessage(Number(id), payload)
                : await createQuickMessage(payload)

            if (res.success) {
                toast.success(
                    isEditing
                        ? 'Mensagem rápida atualizada'
                        : 'Mensagem rápida criada'
                )
                navigate(LIST_ROUTE)
            } else {
                toast.error(res.error || 'Erro ao salvar')
            }
        } catch {
            toast.error('Falha ao salvar a mensagem rápida')
        } finally {
            setSaving(false)
        }
    }

    const allowed = isEditing ? canUpdate : canCreate
    if (!allowed) {
        return (
            <div className="p-6">
                <div className="alert alert-warning">
                    Você não tem permissão para{' '}
                    {isEditing ? 'editar' : 'criar'} mensagens rápidas.
                </div>
            </div>
        )
    }

    if (loading) {
        return (
            <div className="p-6 flex justify-center">
                <span className="loading loading-spinner loading-md" />
            </div>
        )
    }

    return (
        <div className="p-6 space-y-4">
            <div className="flex items-center gap-3">
                <button
                    type="button"
                    className="btn btn-ghost btn-sm btn-square"
                    onClick={() => navigate(LIST_ROUTE)}
                    title="Voltar"
                >
                    <MdArrowBack size={20} />
                </button>
                <div>
                    <h1 className="text-2xl font-bold">
                        {isEditing
                            ? 'Editar mensagem rápida'
                            : 'Nova mensagem rápida'}
                    </h1>
                    <p className="text-sm text-base-content/70">
                        O atendente digita
                        <span className="font-mono mx-1 px-1 rounded bg-base-200">
                            /{sanitizeTrigger(form.trigger) || 'atalho'}
                        </span>
                        no chat e o texto entra no campo de mensagem, pronto
                        para revisar antes de enviar.
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 space-y-4">
                    <div className="card bg-base-100 shadow-sm">
                        <div className="card-body p-5 space-y-4">
                            <h2 className="font-semibold text-base flex items-center gap-2">
                                <MdBolt className="text-secondary" />
                                Conteúdo
                            </h2>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <label className="flex flex-col gap-1.5">
                                    <span className={labelClass}>Atalho</span>
                                    <label className="input input-bordered input-sm flex items-center gap-1">
                                        <span className="text-base-content/50 font-mono">
                                            /
                                        </span>
                                        <input
                                            type="text"
                                            className="grow font-mono"
                                            placeholder="bomdia"
                                            value={form.trigger}
                                            onChange={e =>
                                                setForm(f => ({
                                                    ...f,
                                                    trigger: sanitizeTrigger(
                                                        e.target.value
                                                    ),
                                                }))
                                            }
                                        />
                                    </label>
                                    <span className="text-xs text-base-content/50">
                                        Sem espaços e sem acentos. É o que se
                                        digita depois da barra.
                                    </span>
                                </label>

                                <label className="flex flex-col gap-1.5">
                                    <span className={labelClass}>Título</span>
                                    <input
                                        type="text"
                                        className="input input-bordered input-sm"
                                        placeholder="Saudação da manhã"
                                        value={form.title}
                                        onChange={e =>
                                            setForm(f => ({
                                                ...f,
                                                title: e.target.value,
                                            }))
                                        }
                                    />
                                    <span className="text-xs text-base-content/50">
                                        Como a mensagem aparece na lista do
                                        chat.
                                    </span>
                                </label>
                            </div>

                            {/* Campo e variáveis lado a lado: a variável é
                                inserida no ponto do cursor, então precisa estar
                                à vista enquanto se escreve — não abaixo, fora
                                do campo de visão. */}
                            <div className="flex flex-col md:flex-row gap-3">
                                <label className="flex flex-col gap-1.5 flex-1 min-w-0">
                                    <span className={labelClass}>Mensagem</span>
                                    <textarea
                                        ref={bodyRef}
                                        className="textarea textarea-bordered text-sm leading-relaxed w-full"
                                        rows={10}
                                        value={form.body}
                                        onChange={e =>
                                            setForm(f => ({
                                                ...f,
                                                body: e.target.value,
                                            }))
                                        }
                                        placeholder="Bom dia, {{client.name}}! Como podemos ajudar?"
                                    />
                                </label>

                                {tokens.length > 0 && (
                                    <div className="md:w-56 shrink-0 flex flex-col gap-1.5">
                                        <span className={labelClass}>
                                            Variáveis
                                        </span>
                                        <div className="rounded-lg border border-base-300 bg-base-200/40 p-2 md:max-h-[268px] md:overflow-y-auto space-y-3">
                                            {groupedTokens.map(group => (
                                                <div
                                                    key={group.entity}
                                                    className="space-y-1"
                                                >
                                                    <span className="text-[10px] uppercase tracking-wide text-base-content/50 font-semibold">
                                                        {group.label}
                                                    </span>
                                                    <div className="flex flex-wrap gap-1">
                                                        {group.items.map(
                                                            token => (
                                                                <button
                                                                    key={
                                                                        token.key
                                                                    }
                                                                    type="button"
                                                                    className="btn btn-xs btn-ghost bg-base-100 border border-base-300 font-normal normal-case"
                                                                    title={`{{${token.key}}} · ex: ${token.example}`}
                                                                    // Sem isto o
                                                                    // clique tira o
                                                                    // foco do campo
                                                                    // e a variável
                                                                    // cairia no fim
                                                                    // do texto, não
                                                                    // no cursor.
                                                                    onMouseDown={e =>
                                                                        e.preventDefault()
                                                                    }
                                                                    onClick={() =>
                                                                        insertToken(
                                                                            token.key
                                                                        )
                                                                    }
                                                                >
                                                                    {token.label}
                                                                </button>
                                                            )
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        <span className="text-[11px] text-base-content/50 leading-snug">
                                            Clique para inserir no ponto do
                                            cursor. O valor é preenchido no
                                            momento do uso.
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="card bg-base-100 shadow-sm">
                        <div className="card-body p-5 space-y-3">
                            <h2 className="font-semibold text-base flex items-center gap-2">
                                <MdAttachFile className="text-secondary" />
                                Anexos
                            </h2>
                            <p className="text-xs text-base-content/60">
                                Enviados junto com o texto quando o atendente
                                escolhe esta mensagem. Até 5 arquivos.
                            </p>

                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                accept="image/*,video/*,audio/*,.pdf,.doc,.docx"
                                className="hidden"
                                onChange={handleFileSelect}
                            />

                            {mediaUrls.length > 0 && (
                                <div className="flex flex-wrap gap-2">
                                    {mediaUrls.map((url, index) => (
                                        <div
                                            key={url}
                                            className="badge badge-lg gap-2 badge-secondary"
                                        >
                                            <span className="text-xs truncate max-w-[180px]">
                                                {fileNameFromUrl(url)}
                                            </span>
                                            <button
                                                type="button"
                                                className="btn btn-ghost btn-xs btn-circle"
                                                onClick={() =>
                                                    setMediaUrls(prev =>
                                                        prev.filter(
                                                            (_, i) =>
                                                                i !== index
                                                        )
                                                    )
                                                }
                                            >
                                                <MdClose />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <button
                                type="button"
                                className="btn btn-outline btn-sm gap-2 w-fit"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={uploading || mediaUrls.length >= 5}
                            >
                                {uploading ? (
                                    <span className="loading loading-spinner loading-xs" />
                                ) : (
                                    <MdAttachFile size={18} />
                                )}
                                Adicionar anexo
                            </button>
                        </div>
                    </div>
                </div>

                <div className="space-y-4">
                    <div className="card bg-base-100 shadow-sm">
                        <div className="card-body p-5 space-y-4">
                            <h2 className="font-semibold text-base">
                                Quem pode usar
                            </h2>

                            <label className="flex flex-col gap-1.5">
                                <span className={labelClass}>
                                    Visibilidade
                                </span>
                                <select
                                    className="select select-bordered select-sm"
                                    value={form.visibility}
                                    onChange={e =>
                                        setForm(f => ({
                                            ...f,
                                            visibility: e.target
                                                .value as QuickMessageVisibility,
                                        }))
                                    }
                                >
                                    {VISIBILITIES.map(v => (
                                        <option key={v} value={v}>
                                            {QUICK_MESSAGE_VISIBILITY_LABELS[v]}
                                        </option>
                                    ))}
                                </select>
                                <span className="text-xs text-base-content/50">
                                    {
                                        QUICK_MESSAGE_VISIBILITY_DESCRIPTIONS[
                                            form.visibility
                                        ]
                                    }
                                </span>
                            </label>

                            <label className="flex flex-col gap-1.5">
                                <span className={labelClass}>Setor</span>
                                <select
                                    className="select select-bordered select-sm"
                                    value={form.departmentId}
                                    onChange={e =>
                                        setForm(f => ({
                                            ...f,
                                            departmentId: e.target.value,
                                        }))
                                    }
                                >
                                    <option value="">Todos os setores</option>
                                    {departments.map(d => (
                                        <option key={d.id} value={d.id}>
                                            {d.name}
                                        </option>
                                    ))}
                                </select>
                                <span className="text-xs text-base-content/50">
                                    Restringir evita que textos de um setor
                                    poluam a lista de outro.
                                </span>
                            </label>

                            <label className="flex items-center gap-3 cursor-pointer pt-1">
                                <input
                                    type="checkbox"
                                    className="toggle toggle-sm toggle-secondary"
                                    checked={form.active}
                                    onChange={e =>
                                        setForm(f => ({
                                            ...f,
                                            active: e.target.checked,
                                        }))
                                    }
                                />
                                <span className="flex flex-col">
                                    <span className={labelClass}>Ativa</span>
                                    <span className="text-xs text-base-content/50">
                                        Inativa some do chat sem perder o texto.
                                    </span>
                                </span>
                            </label>
                        </div>
                    </div>

                    <div className="card bg-base-100 shadow-sm">
                        <div className="card-body p-5 space-y-2">
                            <h2 className="font-semibold text-base">
                                Pré-visualização
                            </h2>
                            <div className="rounded-lg bg-base-200 p-3 text-sm whitespace-pre-wrap min-h-[80px]">
                                {preview.trim() || (
                                    <span className="text-base-content/40">
                                        O texto aparece aqui, com as variáveis
                                        substituídas por exemplos.
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="flex gap-2">
                        <button
                            type="button"
                            className="btn btn-ghost btn-sm flex-1"
                            onClick={() => navigate(LIST_ROUTE)}
                            disabled={saving}
                        >
                            Cancelar
                        </button>
                        <button
                            type="button"
                            className="btn btn-secondary btn-sm flex-1 gap-2"
                            onClick={handleSubmit}
                            disabled={saving || uploading}
                        >
                            {saving ? (
                                <span className="loading loading-spinner loading-xs" />
                            ) : (
                                <MdSave size={18} />
                            )}
                            Salvar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}
